/**
 * Phase 129 (Plan 129-05, D-14) — the linked-GitHub-repositories editor, extracted from
 * `ProjectRepositoriesSection` so the PM-only detail-page section and the team Engineering card
 * render one panel with two sets of hooks (no fork). Pure: it owns only the picker and the unlink
 * confirmation state; every read and write comes in through props. It renders no `<form>`, so it
 * is safe inside the project edit page.
 *
 *   Header  : title + helper copy
 *   Body    : <select> of available repos (filtered to ones not yet linked) + Add button
 *             chips for each linked repo, with an unlink button -> ConfirmModal -> unlink.
 */
import { useId, useMemo, useState, type ReactElement } from 'react';
import { ConfirmModal } from '../common/Modal';
import { Spinner } from '../common/Spinner';
import type { GithubRepository, ProjectRepository } from '../../services/api/projectRepositories';

export interface LinkedRepositoriesPanelProps {
  title: string;
  description: string;
  linked: ProjectRepository[] | undefined;
  available: GithubRepository[] | undefined;
  isLoading: boolean;
  error: Error | null | undefined;
  onLink: (githubRepositoryId: string, onSuccess: () => void) => void;
  linkPending: boolean;
  onUnlink: (githubRepositoryId: string, onSuccess: () => void) => void;
  unlinkPending: boolean;
  unlinkError: Error | null | undefined;
  /** Render without the white card chrome and with an h3 heading (used inside another card). */
  embedded?: boolean;
}

export function LinkedRepositoriesPanel({
  title,
  description,
  linked,
  available,
  isLoading,
  error,
  onLink,
  linkPending,
  onUnlink,
  unlinkPending,
  unlinkError,
  embedded = false,
}: LinkedRepositoriesPanelProps): ReactElement {
  const selectId = useId();
  const [pendingId, setPendingId] = useState<string>('');
  const [confirmRepo, setConfirmRepo] = useState<{ id: string; name: string } | null>(null);

  const candidates = useMemo(() => {
    if (!available) return [];
    const linkedIds = new Set((linked ?? []).map((r) => r.repository.id));
    return available.filter((r) => !linkedIds.has(r.id));
  }, [available, linked]);

  const Heading = embedded ? 'h3' : 'h2';

  return (
    <div className={embedded ? '' : 'bg-white rounded-lg border border-gray-200 p-6'}>
      <div className="flex items-baseline justify-between">
        <Heading
          className={
            embedded
              ? 'text-sm font-semibold text-gray-900'
              : 'text-lg font-semibold text-gray-900'
          }
        >
          {title}
        </Heading>
        {isLoading && <Spinner size="sm" />}
      </div>
      <p className="mt-1 text-sm text-gray-500">{description}</p>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load or update repository mappings: {error.message}
        </div>
      )}

      {/* Add form */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label htmlFor={selectId} className="sr-only">
          Repository to link
        </label>
        <select
          id={selectId}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          value={pendingId}
          onChange={(e) => setPendingId(e.target.value)}
          disabled={candidates.length === 0 || linkPending}
        >
          <option value="">— choose repository —</option>
          {candidates.map((r) => (
            <option key={r.id} value={r.id}>
              {r.owner}/{r.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50"
          disabled={!pendingId || linkPending}
          onClick={() => {
            if (!pendingId) return;
            onLink(pendingId, () => setPendingId(''));
          }}
        >
          {linkPending ? 'Adding…' : 'Add'}
        </button>
      </div>

      {/* Chip list of currently-linked repos */}
      <div className="mt-4 flex flex-wrap gap-2">
        {(linked ?? []).length === 0 && !isLoading && (
          <p className="text-sm text-gray-500">No repositories linked yet.</p>
        )}
        {(linked ?? []).map((r) => (
          <span
            key={r.repository.id}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 border border-gray-200"
          >
            <span className="font-mono">
              {r.repository.owner}/{r.repository.name}
            </span>
            <button
              type="button"
              className="ml-1 text-gray-500 hover:text-red-600"
              aria-label={`Unlink ${r.repository.owner}/${r.repository.name}`}
              onClick={() =>
                setConfirmRepo({
                  id: r.repository.id,
                  name: `${r.repository.owner}/${r.repository.name}`,
                })
              }
            >
              ✕
            </button>
          </span>
        ))}
      </div>

      <ConfirmModal
        isOpen={confirmRepo !== null}
        onClose={() => setConfirmRepo(null)}
        onConfirm={() => {
          if (!confirmRepo) return;
          onUnlink(confirmRepo.id, () => setConfirmRepo(null));
        }}
        title="Unlink repository?"
        message={
          confirmRepo
            ? `Unlink ${confirmRepo.name} from this project?\nHistorical commits stay attributed; future commits will no longer count toward this project's engineering metrics.`
            : ''
        }
        confirmText="Unlink"
        variant="danger"
        isLoading={unlinkPending}
        error={unlinkError ? unlinkError.message : null}
      />
    </div>
  );
}

export default LinkedRepositoriesPanel;
